import { getAuthToken, getBidderId } from './user';
import {
    LOAD_BIDDER_DATA_SUCCESS,
    LOAD_LIVE_CATALOG_REGISTRATION_FAIL,
    LOAD_LIVE_CATALOG_REGISTRATION_REQUEST,
    LOAD_LIVE_CATALOG_REGISTRATION_SUCCESS,
    LOAD_LIVE_CATALOG_REGISTRATION_SUCCESS_ACTION,
    LOAD_UPCOMING_CATALOG_REGISTRATION_FAIL,
    LOAD_UPCOMING_CATALOG_REGISTRATION_REQUEST,
    LOAD_UPCOMING_CATALOG_REGISTRATION_SUCCESS,
    LOG_OUT_BIDDER,
    LOGIN_SUCCESS,
    REGISTER_FOR_CATALOG_SUCCESS,
} from './actions';
import { ApprovalStatus } from '@/types/Approval';
import cloneDeep from 'lodash/cloneDeep';
import { createSelector } from 'reselect';
import difference from 'lodash/difference';
import { fetchBidLimitBalance } from '@/redux/api/bidLimit';
import { fetchLiveCatalogStatus } from '@/redux/api/liveCatalogStatus';
import { getDeployment } from './config';
import { getLiveCatalogData } from './console';
import { GlobalState } from '../rootReducer';
import { PASSWORD_RESET_SUCCESS } from './passwordReset';
import { postUpcomingCatalogRegistration } from '@/redux/api/catalogRegistration';
import union from 'lodash/union';

interface CatalogRegistration {
    approved: boolean;
    bidLimit: number;
    blockedByAuctioneer: boolean;
    catalogId: number;
    declined: boolean;
    limited: boolean;
    pending: boolean;
    registered: boolean;
    suspended: boolean;
}

const createCatalogRegistration = (
    catalogId: number,
    approvalStatus: ApprovalStatus,
    bidLimit = 0
): CatalogRegistration => ({
    approved: approvalStatus === ApprovalStatus.Approved,
    bidLimit: bidLimit,
    blockedByAuctioneer: approvalStatus === ApprovalStatus.BlockedByAuctioneer,
    catalogId: catalogId,
    declined: approvalStatus === ApprovalStatus.Declined,
    limited: approvalStatus === ApprovalStatus.Limited,
    pending: approvalStatus === ApprovalStatus.Pending,
    registered: true,
    suspended: approvalStatus === ApprovalStatus.Suspended,
});

interface DefaultState {
    readonly byId: {
        [catalogId: number]: CatalogRegistration;
    };
    readonly loaded: {
        [catalogId: number]: number;
    };
    readonly loading: number[];
}

/* reducer */
const DEFAULT_STATE: DefaultState = {
    byId: {},
    loaded: {},
    loading: [],
};

export type State = typeof DEFAULT_STATE;

export default function reducer(state: State = DEFAULT_STATE, action: any = {}) {
    let existing: DefaultState['byId'];
    let loaded: DefaultState['loaded'];
    let loading: DefaultState['loading'];
    let time: number;

    switch (action.type) {
        case LOAD_LIVE_CATALOG_REGISTRATION_FAIL:
        case LOAD_UPCOMING_CATALOG_REGISTRATION_FAIL:
            return {
                ...state,
                loading: difference(state.loading, [action.meta.catalogId]),
            };
        case LOAD_LIVE_CATALOG_REGISTRATION_REQUEST:
        case LOAD_UPCOMING_CATALOG_REGISTRATION_REQUEST:
            return {
                ...state,
                loading: union(state.loading, [action.payload]),
            };
        case REGISTER_FOR_CATALOG_SUCCESS:
            return {
                ...state,
                byId: {
                    ...state.byId,
                    [action.payload.catalogId]: createCatalogRegistration(
                        action.payload.catalogId,
                        action.payload.approvalStatus,
                        action.payload.bidLimit
                    ),
                },
            };
        case LOAD_BIDDER_DATA_SUCCESS:
        case LOGIN_SUCCESS:
        case PASSWORD_RESET_SUCCESS:
        case LOAD_UPCOMING_CATALOG_REGISTRATION_SUCCESS:
            time = action.meta.actionTime;
            loaded = { ...state.loaded };
            loading = [...state.loading];
            loading = difference(loading, [action.meta.catalogId]);
            existing = cloneDeep(state.byId);

            if (action.payload.catalogRegistration) {
                action.payload.catalogRegistration.forEach((catalog) => {
                    existing[catalog.catalogId] = catalog;
                    existing[catalog.catalogId].registered =
                        catalog.approved ||
                        catalog.blockedByAuctioneer ||
                        catalog.declined ||
                        catalog.limited ||
                        catalog.pending ||
                        catalog.suspended;
                    loaded[catalog.catalogId] = time;
                });
            }

            return {
                ...state,
                byId: existing,
                loaded,
                loading,
            };
        case LOAD_LIVE_CATALOG_REGISTRATION_SUCCESS:
            time = action.meta.actionTime;
            loaded = { ...state.loaded, [action.meta.catalogId]: time };
            loading = [...state.loading];
            loading = difference(loading, [action.meta.catalogId]);
            const existingCatalog = state.byId[action.meta.catalogId] || {};
            existing = cloneDeep(state.byId);
            existing[action.meta.catalogId] = existing[action.meta.catalogId] || ({} as CatalogRegistration);
            existing[action.meta.catalogId].bidLimit = action.payload.bidLimit
                ? action.payload.bidLimit
                : existing[action.meta.catalogId].bidLimit;

            if (action.payload.approved && !(existingCatalog as CatalogRegistration).approved) {
                existing[action.meta.catalogId].approved = true;
                existing[action.meta.catalogId].pending = false;
                existing[action.meta.catalogId].declined = false;
                existing[action.meta.catalogId].registered = true;
                return {
                    ...state,
                    byId: existing,
                    loaded,
                    loading,
                };
            } else if (!action.payload.approved && (existingCatalog as CatalogRegistration).approved) {
                existing[action.meta.catalogId].approved = false;
                existing[action.meta.catalogId].pending = false;
                existing[action.meta.catalogId].declined = true;
                existing[action.meta.catalogId].registered = false;
                existing[action.meta.catalogId].bidLimit = 0;
                existing[action.meta.catalogId].limited = false;
                return {
                    ...state,
                    byId: existing,
                    loaded,
                    loading,
                };
            }

            return {
                ...state,
                byId: existing,
                loaded,
                loading,
            };

        case LOG_OUT_BIDDER:
            return {
                ...DEFAULT_STATE,
            };

        default:
            return state;
    }
}

/* SELECTORS */
const stateSelector = (state: GlobalState) => state.catalogRegistration;
const idSelector = (state: GlobalState, id) => id;

const byIdSelector = createSelector(stateSelector, (state: State) => state.byId);

const loadingSelector = createSelector(stateSelector, (state: State) => state.loading);

export const getCatalogRegistration: (state: GlobalState, selector: any) => CatalogRegistration = createSelector(
    [byIdSelector, idSelector],
    (byId, id): CatalogRegistration => {
        return (
            byId[id] || {
                approved: false,
                bidLimit: 0,
                blockedByAuctioneer: false,
                catalogId: id,
                declined: false,
                limited: false,
                pending: false,
                registered: false,
                suspended: false,
            }
        );
    }
);

const isCatalogRegistrationLoading = createSelector([loadingSelector, idSelector], (loading, id) =>
    loading.includes(id)
);

export const getAllCurrentUserCatalogRegistrationIds = (state: GlobalState) => {
    return Object.keys(state.catalogRegistration.byId)
        .filter((key) => state.catalogRegistration.byId[key].status !== 'done')
        .map<number>((catIdString) => Number(catIdString));
};

const fetchLiveCatalogRegistration =
    (catalogId: number, sellerId: number) => async (dispatch: Function, getState: Function) => {
        const state = getState();
        try {
            const authToken = getAuthToken(state);
            const deployment = getDeployment(state);
            const { approved: wasApproved } = getLiveCatalogData(state, catalogId);
            const bidderId = getBidderId(state);

            dispatch({
                meta: { actionTime: Date.now() },
                payload: catalogId,
                type: LOAD_LIVE_CATALOG_REGISTRATION_REQUEST,
            });

            const liveAuctionState = await fetchLiveCatalogStatus({ authToken, catalogId, deployment, sellerId });
            const bidderApproval = await fetchBidLimitBalance({ authToken, bidderId, catalogId, deployment });

            const payload = {
                approved:
                    bidderApproval.payload.approvalStatus === ApprovalStatus.Approved ||
                    bidderApproval.payload.approvalStatus === ApprovalStatus.Limited,
                bidLimit: bidderApproval.payload.bidLimit,
                catalogId,
                created: new Date().getTime(),
                publishKey: liveAuctionState?.data?.bidder.subscriptions.pubnub.publishKey || '',
                wasApproved,
            };

            dispatch({
                meta: { actionTime: Date.now(), catalogId },
                payload,
                type: LOAD_LIVE_CATALOG_REGISTRATION_SUCCESS,
            } as LOAD_LIVE_CATALOG_REGISTRATION_SUCCESS_ACTION);
        } catch (error) {
            dispatch({
                error: true,
                meta: { catalogId },
                payload: error,
                type: LOAD_LIVE_CATALOG_REGISTRATION_FAIL,
            });
        }
    };

const shouldFetchLiveCatalogRegistration = (state: GlobalState, catalogId: number): boolean => {
    const authToken = getAuthToken(state);
    if (!catalogId || !authToken) {
        return false;
    }
    const loading = isCatalogRegistrationLoading(state, catalogId);
    return !loading;
};

export const fetchLiveCatalogRegistrationIfNeeded =
    (catalogId: number, sellerId: number, force: boolean = false) =>
    async (dispatch: Function, getState: Function) => {
        const shouldFetch = shouldFetchLiveCatalogRegistration(getState(), catalogId);
        if (force || shouldFetch) {
            return dispatch(fetchLiveCatalogRegistration(catalogId, sellerId));
        }
        return Promise.resolve();
    };

const fetchUpcomingCatalogRegistration = (catalogId: number) => async (dispatch: Function, getState: Function) => {
    const state = getState();
    try {
        const authToken = getAuthToken(state);
        const deployment = getDeployment(state);

        if (authToken) {
            dispatch({
                payload: catalogId,
                type: LOAD_UPCOMING_CATALOG_REGISTRATION_REQUEST,
            });
            const response = await postUpcomingCatalogRegistration({ authToken, catalogId, deployment });
            return dispatch({
                meta: { actionTime: Date.now(), catalogId },
                payload: { catalogRegistration: response.data },
                type: LOAD_UPCOMING_CATALOG_REGISTRATION_SUCCESS,
            });
        }
        return Promise.resolve();
    } catch (error) {
        dispatch({
            error: true,
            meta: { catalogId },
            payload: error,
            type: LOAD_UPCOMING_CATALOG_REGISTRATION_FAIL,
        });
    }
};

const shouldFetchUpcomingCatalogRegistration = (state, catalogId) => {
    const authToken = getAuthToken(state);
    if (!catalogId || !authToken) {
        return false;
    }
    const loading = isCatalogRegistrationLoading(state, catalogId);
    return !loading;
};

export const fetchUpcomingCatalogRegistrationIfNeeded =
    (catalogId: number, force: boolean = false) =>
    async (dispatch: Function, getState: Function) => {
        if (force || shouldFetchUpcomingCatalogRegistration(getState(), catalogId)) {
            return dispatch(fetchUpcomingCatalogRegistration(catalogId));
        }
        return Promise.resolve();
    };
